import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import useActionDuration from '../../../../../common/hooks/useActionDuration';
import { getAnnotationStyle } from '../../../../../common/utils/utils';
import { isVideoLabelingM4EnabledAtom } from '../../../../labelling-v2/atoms/m4-atoms';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { useDynamicAttributesText } from '../../../../labelling-v2/hooks/use-dynamic-attributes-text';
import type { ANNOTATION_SHAPE_STYLING_TYPE } from '../../../constants';
import { ANNOTATION_TYPE } from '../../../constants';
import useStage from '../../../providers/hooks/use-stage';
import useTutorial from '../../../providers/hooks/use-tutorials';
import { type StagePointerEventT } from '../../../providers/stage-provider';
import { CUBOID_TUTORIAL_CONFIG } from '../../../tutorials/cuboid-tutorial';
import { TOOLBAR_TUTORIAL } from '../../../tutorials/toolbar-tutorial';
import { type CuboidAnnotationT } from '../../../types';
import { isLabelingMode } from '../../toolbar/utils';
import Shape from '../shape/shape';
import { getStylesForStylingType } from '../utils';
import { convertPointsForRender, CUBOID_FACE, getCuboidOnDrawing, getCuboidSideFaces, getCuboidVertices, getInitialCuboidPoints, getTransformedCuboidOnVertexMove, getTranslatedCuboid, NUMBER_OF_PRIMARY_VERTICES } from './utils';
const CUBOID_TOOLBAR_TUTORIAL_ID = 'select-tool-CUBOID';
type PropsT = {
  isDrawing?: boolean;
  annotation?: CuboidAnnotationT;
  annotationIdx?: string;
  disableChanges?: boolean;
  stylingType?: (typeof ANNOTATION_SHAPE_STYLING_TYPE)[keyof typeof ANNOTATION_SHAPE_STYLING_TYPE];
};
const Cuboid = ({
  isDrawing,
  annotation,
  annotationIdx,
  disableChanges,
  stylingType
}: PropsT) => {
  const {
    stage,
    addAnnotation,
    updateAnnotation,
    getStageCenter,
    getStageDimension
  } = useStage();
  const {
    editorModeAtom,
    editableAnnotationAtom,
    focusedAnnotationIdAtom,
    selectedAnnotationLabelAtom
  } = useStageAtoms();
  const editorMode = useAtomValue(editorModeAtom);
  const editableAnnotation = useAtomValue(editableAnnotationAtom);
  const focusedAnnotationId = useAtomValue(focusedAnnotationIdAtom);
  const annotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const isVideoLabelingM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const [cuboid, setCuboid] = useState<CuboidAnnotationT | undefined | null>(annotation);
  const [labelCoords, setLabelCoords] = useState(null);
  const [initialPoint, setInitialPoint] = useState([0, 0]);
  const [currentFace, setCurrentFace] = useState(null);
  const cuboidFrontFaceRef = useRef<SVGPolygonElement | null>(null);
  const cuboidBackFaceRef = useRef<SVGPolygonElement | null>(null);
  const cuboidRef = useRef<SVGGElement>(null);
  const isLocked = Boolean(annotation?.locked);
  const [selectedVertexIdx, setSelectedVertexIdx] = useState(null);
  const isEditable = editableAnnotation === annotationIdx;
  const isFocused = focusedAnnotationId === annotationIdx;
  const {
    start,
    end
  } = useActionDuration();
  const {
    addStep,
    next,
    currentActiveTutorial
  } = useTutorial();
  useEffect(() => {
    const rootTutorialId = TOOLBAR_TUTORIAL[CUBOID_TOOLBAR_TUTORIAL_ID]?.id;
    if (isDrawing && currentActiveTutorial === rootTutorialId) {
      rootTutorialId && addStep({
        ...CUBOID_TUTORIAL_CONFIG.drawCuboid,
        getTarget: () => cuboidFrontFaceRef.current
      }, rootTutorialId);
    }
  }, [addStep, currentActiveTutorial, isDrawing]);
  useEffect(() => {
    setCuboid(annotation);
  }, [annotation]);
  useEffect(() => {
    if (!isDrawing && cuboid) {
      // @ts-expect-error missing null check
      setLabelCoords([(cuboid.data.points[0][0] + cuboid.data.points[2][0]) / 2, (cuboid.data.points[0][1] + cuboid.data.points[2][1]) / 2]);
    }
  }, [isDrawing, cuboid]);
  const onPointerDown = useCallback((stageEvent: StagePointerEventT) => {
    const {
      mousePos
    } = stageEvent.detail;

    // TODO: handle case where annotationLabel is not selected
    if (!annotationLabel) return;

    // Clamp mouse position to stage bounds
    const [stageWidth, stageHeight] = getStageDimension();
    const clampedX = Math.max(0, Math.min(mousePos[0], stageWidth));
    const clampedY = Math.max(0, Math.min(mousePos[1], stageHeight));
    const clampedMousePos: [number, number] = [clampedX, clampedY];
    if (cuboid) {
      next(CUBOID_TUTORIAL_CONFIG.endCuboid.id);
      // complete cuboid and add annotation
      addAnnotation(cuboid, {
        actionDuration: end()
      });
      setCuboid(null);
      // @ts-expect-error missing null check
      setCurrentFace(false);
      return;
    }
    const {
      points
    } = getInitialCuboidPoints(clampedMousePos);
    const initialCuboid: CuboidAnnotationT = {
      annotationLabel,
      annotationType: ANNOTATION_TYPE.CUBOID,
      data: {
        points
      }
    };

    // @ts-expect-error missing null check
    setCurrentFace(CUBOID_FACE.INITIAL);
    setCuboid(initialCuboid);
    setInitialPoint(clampedMousePos);
    start();
  }, [annotationLabel, cuboid, next, start, addAnnotation, end, getStageDimension]);
  const onPointerMove = useCallback((stageEvent: StagePointerEventT) => {
    if (!cuboid || !currentFace) return;
    const {
      mousePos
    } = stageEvent.detail;

    // Clamp mouse position to stage bounds during drawing
    const [stageWidth, stageHeight] = getStageDimension();
    const clampedX = Math.max(0, Math.min(mousePos[0], stageWidth));
    const clampedY = Math.max(0, Math.min(mousePos[1], stageHeight));
    const clampedMousePos: [number, number] = [clampedX, clampedY];
    setCuboid(getCuboidOnDrawing(currentFace, (initialPoint as [number, number]), clampedMousePos, cuboid, stageWidth, stageHeight));
  }, [cuboid, initialPoint, currentFace, getStageDimension]);
  const onPointerUp = useCallback((stageEvent: StagePointerEventT) => {
    if (cuboid) {
      const {
        mousePos
      } = stageEvent.detail;

      // Clamp mouse position to stage bounds
      const [stageWidth, stageHeight] = getStageDimension();
      const clampedX = Math.max(0, Math.min(mousePos[0], stageWidth));
      const clampedY = Math.max(0, Math.min(mousePos[1], stageHeight));
      const clampedMousePos: [number, number] = [clampedX, clampedY];
      const rootTutorialId = TOOLBAR_TUTORIAL[CUBOID_TOOLBAR_TUTORIAL_ID]?.id;
      rootTutorialId && addStep({
        ...CUBOID_TUTORIAL_CONFIG.endCuboid,
        getTarget: () => cuboidBackFaceRef.current
      }, rootTutorialId);
      next(CUBOID_TUTORIAL_CONFIG.drawCuboid.id);
      setInitialPoint(clampedMousePos);
      // @ts-expect-error missing null check
      setCurrentFace(CUBOID_FACE.FINAL);
    }
  }, [addStep, cuboid, next, getStageDimension]);
  useEffect(() => {
    if (!stage || !isLabelingMode(editorMode)) return;
    if (isDrawing) {
      stage.addEventListener('pointerdown', onPointerDown);
      stage.addEventListener('pointermove', onPointerMove);
      stage.addEventListener('pointerup', onPointerUp);
      return () => {
        stage.removeEventListener('pointerdown', onPointerDown);
        stage.removeEventListener('pointermove', onPointerMove);
        stage.removeEventListener('pointerup', onPointerUp);
      };
    }
  }, [isDrawing, stage, onPointerDown, onPointerMove, onPointerUp, editorMode]);
  const onDragStart = useCallback((stageEvent: StagePointerEventT) => {
    setInitialPoint(stageEvent.detail.mousePos);
  }, [setInitialPoint]);
  const onDrag = useCallback((stageEvent: StagePointerEventT) => {
    if (annotation) {
      // For move drag, if the target is not same ignore the event.
      if (!selectedVertexIdx && stageEvent.detail.dragTarget?.parentElement !== cuboidRef.current) {
        return;
      }
      const [stageWidth, stageHeight] = getStageDimension();
      const newCuboid = selectedVertexIdx !== null ? getTransformedCuboidOnVertexMove(annotation, selectedVertexIdx,
      // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'number[]' is not assignable to parameter of type 'MousePosT'.
      initialPoint, stageEvent.detail.mousePos) : getTranslatedCuboid(stageEvent.detail.deltaX, stageEvent.detail.deltaY, annotation, stageWidth, stageHeight);
      setCuboid(newCuboid);
      start();
    }
  }, [selectedVertexIdx, annotation, initialPoint, start, getStageDimension]);
  const onDragEnd = useCallback((stageEvent: StagePointerEventT) => {
    if (annotation && annotationIdx !== undefined) {
      // For move drag, if the target is not same ignore the event.
      if (!selectedVertexIdx && stageEvent.detail.dragTarget?.parentElement !== cuboidRef.current) {
        return;
      }
      const [stageWidth, stageHeight] = getStageDimension();
      const newCuboid = selectedVertexIdx !== null ? getTransformedCuboidOnVertexMove(annotation, selectedVertexIdx,
      // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'number[]' is not assignable to parameter of type 'MousePosT'.
      initialPoint, stageEvent.detail.mousePos) : getTranslatedCuboid(stageEvent.detail.deltaX, stageEvent.detail.deltaY, annotation, stageWidth, stageHeight);
      setCuboid(newCuboid);
      updateAnnotation(annotationIdx, newCuboid, {
        actionDuration: end()
      });
      setSelectedVertexIdx(null);
    }
  }, [selectedVertexIdx, annotation, annotationIdx, updateAnnotation, initialPoint, end, getStageDimension]);
  useEffect(() => {
    if (!stage || disableChanges || isLocked || !isLabelingMode(editorMode)) return;
    if (isEditable) {
      stage.addEventListener('dragstart', onDragStart);
      stage.addEventListener('drag', onDrag);
      stage.addEventListener('dragend', onDragEnd);
      return () => {
        stage.removeEventListener('dragstart', onDragStart);
        stage.removeEventListener('drag', onDrag);
        stage.removeEventListener('dragend', onDragEnd);
      };
    }
  }, [isEditable, stage, onDragStart, onDrag, onDragEnd, disableChanges, editorMode, isLocked]);
  const dynamicAttributesText = useDynamicAttributesText(annotationIdx);
  if (!cuboid) {
    const stageCenter = getStageCenter();
    return <polygon ref={cuboidFrontFaceRef} points={`${stageCenter[0]},${stageCenter[1]}`}></polygon>;
  }
  const vertices = getCuboidVertices(cuboid);
  const {
    left,
    right,
    top,
    bottom
  } = getCuboidSideFaces(cuboid);
  const svgStyles = {
    stroke: cuboid.annotationLabel.style.strokeColor,
    fill: cuboid.annotationLabel.style.fillColor,
    fillOpacity: 0.3,
    strokeLinejoin: ('round' as const),
    ...getStylesForStylingType(stylingType)
  };
  const annotationStyle = getAnnotationStyle(svgStyles, {
    isFocused,
    isLocked,
    useStageStrokeWidth: true,
    isM4: isVideoLabelingM4Enabled
  });
  return <Shape
  // @ts-expect-error todo(ts-migration) TS2322 Type '{ point: number[]; type: string; }[]' is not assignable to type 'VertexT[]'.
  vertices={vertices} isLocked={isLocked} isDrawing={isDrawing} metadataPillText={dynamicAttributesText} annotationLabel={{
    ...cuboid.annotationLabel,
    labelCoords
  }} annotationIdx={annotationIdx}
  // @ts-expect-error missing null check
  onVertexSelect={(_, idx) => setSelectedVertexIdx(idx)} dataAttributes={{
    'data-e2e-id': `annotation-${cuboid.annotationLabel.title}-${cuboid.annotationType}`,
    'data-annotation-id': annotationIdx?.toString() || ''
  }}>
      <g ref={cuboidRef}>
        <polygon ref={cuboidFrontFaceRef} points={convertPointsForRender(cuboid.data.points.slice(0, NUMBER_OF_PRIMARY_VERTICES / 2))} {...annotationStyle} />
        <polygon ref={cuboidBackFaceRef} points={convertPointsForRender(cuboid.data.points.slice(NUMBER_OF_PRIMARY_VERTICES / 2))} {...annotationStyle} />
        {/* @ts-expect-error todo(ts-migration) TS2345 Argument of type '([number, number] | undefined)[]' is not assignable to parameter of type '[number, number][]'. */}
        <polygon points={convertPointsForRender(left)} {...annotationStyle} />
        {/* @ts-expect-error todo(ts-migration) TS2345 Argument of type '([number, number] | undefined)[]' is not assignable to parameter of type '[number, number][]'. */}
        <polygon points={convertPointsForRender(right)} {...annotationStyle} />
        {/* @ts-expect-error todo(ts-migration) TS2345 Argument of type '([number, number] | undefined)[]' is not assignable to parameter of type '[number, number][]'. */}
        <polygon points={convertPointsForRender(top)} {...annotationStyle} />
        {/* @ts-expect-error todo(ts-migration) TS2345 Argument of type '([number, number] | undefined)[]' is not assignable to parameter of type '[number, number][]'. */}
        <polygon points={convertPointsForRender(bottom)} {...annotationStyle} />
      </g>
    </Shape>;
};
export default Cuboid;